import type { FinancialValueDisplayPreference, FinancialValueDisplayScale } from '@/types/financialValueDisplay';
import { DEFAULT_FINANCIAL_VALUE_DISPLAY } from '@/types/financialValueDisplay';

export type FinancialValueKind = 'currency' | 'number';

export interface FinancialValueFormatOptions {
    currency?: string;
    kind?: FinancialValueKind;
    scalable?: boolean;
}

export interface FinancialValuePresentation {
    abbreviated: boolean;
    disclosureRequired: boolean;
    exact: string;
    format: Intl.NumberFormatOptions;
    formatted: string;
    prefix: string;
    scaledValue: number;
    suffix: string;
}

export interface FinancialValueDisplayResolution {
    preference: FinancialValueDisplayPreference;
    source: 'account' | 'surface' | 'system' | 'workspace';
}

export const resolveFinancialValueDisplayPreference = ({
    account,
    surface,
    workspace,
}: {
    account?: FinancialValueDisplayPreference | null;
    surface?: FinancialValueDisplayPreference | null;
    workspace?: FinancialValueDisplayPreference | null;
}): FinancialValueDisplayResolution => {
    if (surface) {
        return { preference: surface, source: 'surface' };
    }
    if (account) {
        return { preference: account, source: 'account' };
    }
    if (workspace) {
        return { preference: workspace, source: 'workspace' };
    }

    return { preference: DEFAULT_FINANCIAL_VALUE_DISPLAY, source: 'system' };
};

interface ScaleDefinition {
    divisor: number;
    scale: Exclude<FinancialValueDisplayScale, 'auto'>;
    suffix: string;
}

const SCALE_DEFINITIONS: readonly ScaleDefinition[] = [
    { divisor: 1, scale: 'units', suffix: '' },
    { divisor: 1_000, scale: 'thousands', suffix: 'k' },
    { divisor: 1_000_000, scale: 'millions', suffix: ' mln' },
    { divisor: 1_000_000_000, scale: 'billions', suffix: ' bln' },
    { divisor: 1_000_000_000_000, scale: 'trillions', suffix: ' tln' },
];

const scaleDefinition = (value: number, preference: FinancialValueDisplayPreference, scalable: boolean) => {
    if (!scalable) {
        return SCALE_DEFINITIONS[0];
    }

    if (preference.scale !== 'auto') {
        return SCALE_DEFINITIONS.find(({ scale }) => scale === preference.scale) ?? SCALE_DEFINITIONS[0];
    }

    const magnitude = Math.abs(value);
    let index = SCALE_DEFINITIONS.findLastIndex(({ divisor }) => magnitude >= divisor);
    index = Math.max(0, index);

    while (index < SCALE_DEFINITIONS.length - 1) {
        const rounded = Number((magnitude / SCALE_DEFINITIONS[index].divisor).toFixed(preference.decimalPlaces));

        if (rounded < 1_000) {
            break;
        }

        index += 1;
    }

    return SCALE_DEFINITIONS[index];
};

const numberFormatOptions = (
    preference: FinancialValueDisplayPreference,
    options: FinancialValueFormatOptions,
): Intl.NumberFormatOptions => ({
    currency: options.kind === 'number' ? undefined : (options.currency ?? 'USD'),
    currencyDisplay: options.kind === 'number' ? undefined : 'narrowSymbol',
    maximumFractionDigits: preference.decimalPlaces,
    minimumFractionDigits: 0,
    style: options.kind === 'number' ? 'decimal' : 'currency',
    useGrouping: true,
});

const exactFormatOptions = (options: FinancialValueFormatOptions): Intl.NumberFormatOptions => ({
    currency: options.kind === 'number' ? undefined : (options.currency ?? 'USD'),
    currencyDisplay: options.kind === 'number' ? undefined : 'narrowSymbol',
    maximumFractionDigits: 20,
    minimumFractionDigits: options.kind === 'number' ? 0 : 2,
    style: options.kind === 'number' ? 'decimal' : 'currency',
    useGrouping: true,
});

export const resolveFinancialValuePresentation = (
    value: number,
    preference: FinancialValueDisplayPreference,
    options: FinancialValueFormatOptions = {},
): FinancialValuePresentation => {
    const resolvedOptions = { ...options, kind: options.kind ?? 'currency' };
    const scale = scaleDefinition(value, preference, options.scalable ?? true);
    const isAccountingNegative = value < 0 && preference.negativeStyle === 'accounting';
    const scaledValue = (isAccountingNegative ? Math.abs(value) : value) / scale.divisor;
    const format = numberFormatOptions(preference, resolvedOptions);
    const prefix = isAccountingNegative ? '(' : '';
    const suffix = `${scale.suffix}${isAccountingNegative ? ')' : ''}`;
    const exactMagnitude = new Intl.NumberFormat('en-US', exactFormatOptions(resolvedOptions)).format(
        isAccountingNegative ? Math.abs(value) : value,
    );
    const exact = isAccountingNegative ? `(${exactMagnitude})` : exactMagnitude;
    const formatted = `${prefix}${new Intl.NumberFormat('en-US', format).format(scaledValue)}${suffix}`;
    const displayedMagnitude = Number(Math.abs(scaledValue).toFixed(preference.decimalPlaces)) * scale.divisor;

    return {
        abbreviated: scale.divisor > 1,
        disclosureRequired: scale.divisor > 1 || displayedMagnitude !== Math.abs(value),
        exact,
        format,
        formatted,
        prefix,
        scaledValue,
        suffix,
    };
};

export const formatFinancialValue = (
    value: number,
    preference: FinancialValueDisplayPreference,
    options?: FinancialValueFormatOptions,
) => resolveFinancialValuePresentation(value, preference, options).formatted;
